import React from 'react';
import { createPortal } from 'react-dom';
import type { Language } from '../types';
import XIcon from './icons/XIcon';
import InfoIcon from './icons/InfoIcon';
import { getTranslation, isRTL } from '../utils/translations';

interface AllergyFilterInfoModalProps {
    isOpen: boolean;
    onClose: () => void;
    language?: Language;
}

const AllergyFilterInfoModal: React.FC<AllergyFilterInfoModalProps> = ({
    isOpen, onClose, language = 'en',
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const rtl = isRTL(language);

    if (!isOpen) return null;

    return createPortal(
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[520] flex items-center justify-center p-4 animate-fade-in"
            onClick={onClose}
        >
            <div
                className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-3xl shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden"
                dir={rtl ? 'rtl' : 'ltr'}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 pb-4">
                    <div className="flex items-start justify-between gap-3 mb-4">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-full bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
                                <InfoIcon className="w-5 h-5" />
                            </div>
                            <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 text-start">
                                {t('allergyFilterInfoTitle')}
                            </h3>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-8 h-8 rounded-full bg-gray-50 dark:bg-slate-800 text-gray-500 dark:text-slate-400 flex items-center justify-center shrink-0"
                            aria-label={t('close')}
                        >
                            <XIcon className="w-4 h-4" />
                        </button>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed text-start mb-4">
                        {t('allergyFilterInfoBody')}
                    </p>
                    <p className="text-xs text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-xl p-3 leading-relaxed text-start">
                        {t('allergyFilterDisclaimer')}
                    </p>
                </div>
                <div className="px-6 pb-6">
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-full py-3 bg-brand-orange text-white font-bold rounded-xl hover:opacity-90 transition-opacity"
                    >
                        {t('gotIt')}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default AllergyFilterInfoModal;
